// Week grouping for the Billing tab's Transactions view.
//
// Labor and equipment bill a week at a time, and a week is signed a week at
// a time (the client's week summary — billingTicketDocs.signedByDate). So
// when the grid is cut to labor or equipment, "group by PO" says nothing:
// labor never rides a PO and the fleet's usage rows all sit under one. This
// grouping answers the question the biller is actually asking — what is
// ready to bill this week, person by person (unit by unit for equipment)?
//
//   Week ending 9/13            [Signed]   hrs / cost / bill
//     Doe, John                 [Signed]   …
//     Smith, Jane               [Signed]   …
//     Not billable              burden, taxes, insurance · contract-blocked …
//
// The trailing Not billable bucket holds two kinds of line: rows the grid
// already shows as Not Billable (a contract rule, a hand flag — still
// selectable, so the flag can be lifted), and the labor cost the grid never
// shows at all: 513xx burden, taxes and insurance and any other 51xxx cost
// type outside the billable three. Those ride along read-only (`info`), so
// the week's labor cost reads whole without any of it being pickable.

import moment from 'moment';
import { weekEnding } from './billingTicketDocs';

export const NB_GROUP_LABEL = 'Not billable';

/** Only labor and equipment ride field tickets — the rows a signature can cover. */
export const ticketed = (t) => t?.cls === 'Labor' || t?.cls === 'Equipment';

/** Who a line groups under: the unit for equipment, the person otherwise. */
export const unitOf = (t) => {
    if (t.cls === 'Equipment') return t.eq || t.who || '(no unit)';
    return t.who || '(no name)';
};

// ── A person's week of ST, a person's week of OT ──
//
// Payroll books labor a day at a time — one 51100 straight-time line and one
// 51200 overtime line per person per day — but the biller reads a person's
// week: so many ST hours, so many OT hours. foldWeekLabor rolls a person's
// week of lines up into one row per cost type (and cost code): ST stays ST,
// OT stays OT. The rolled row's `parts` are the real lines, oldest day first
// — selection, the billable toggle, classification and history all act on
// the parts, and the grid can open the row to show them, so nothing saved
// ever keys off the rolled row.
const FOLD_CT = ['51100', '51200'];

const round2 = (n) => Math.round(n * 100) / 100;
const foldLines = (lines) => {
    const parts = [...lines].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const sum = (k) => parts.reduce((s, p) => s + (p[k] || 0), 0);
    const f = parts[0];
    return {
        ...f,
        key: `fold:${parts.map((p) => p.key).join('+')}`,
        parts,
        dateFrom: f.date,
        dateTo: parts[parts.length - 1].date,
        days: new Set(parts.map((p) => p.date)).size,
        hrs: sum('hrs'),
        // Cents, not float sums — money() truncates, so 420.36 + 646.09
        // would otherwise print a penny short.
        cost: round2(sum('cost')),
        bill: round2(sum('bill')),
        hist: parts.flatMap((p) => p.hist || []),
        cr: (parts.find((p) => p.cr && p.cr.effect === 'warn') || f).cr,
    };
};

/**
 * Rolls one person's week of ST (51100) and OT (51200) lines into one row
 * per cost type and cost code, the rolled row sitting where its first line
 * did. Lines only roll together when they'd read the same in every other
 * column — summary, billable, classification, and the caller's status /
 * review match (`sameKey`) — so a day already on a summary, flagged, or
 * reviewed apart from the rest stays its own row. A lone line is left as is.
 * Everything else (per diem, burden, equipment) passes through untouched.
 */
export const foldWeekLabor = (rows, sameKey = () => '') => {
    const keyOf = (t) => (t.cls === 'Labor' && FOLD_CT.includes(t.ct) && !t.info
        ? [weekEnding(t.date), t.ct, t.cc, t.sid || t.who, t.inv || t.nbInv || '', t.nb ? 1 : 0, t.jde || '', t.ba || '', t.lcOv || '', sameKey(t)].join('|')
        : null);
    const buckets = new Map();
    (rows || []).forEach((t) => {
        const k = keyOf(t);
        if (!k) return;
        if (!buckets.has(k)) buckets.set(k, []);
        buckets.get(k).push(t);
    });
    const out = [];
    const done = new Set();
    (rows || []).forEach((t) => {
        const k = keyOf(t);
        const b = k ? buckets.get(k) : null;
        if (!b || b.length < 2) { out.push(t); return; }
        if (done.has(k)) return;
        done.add(k);
        out.push(foldLines(b));
    });
    return out;
};

/** "Week ending 9/13 · Mon 9/7 – Sun 9/13" */
export const weekLabel = (we) => {
    const end = moment(we, 'YYYY-MM-DD', true);
    if (!end.isValid()) return 'No work date';
    const start = end.clone().subtract(6, 'days');
    return `Week ending ${end.format('M/D')} · ${start.format('ddd M/D')} – ${end.format('ddd M/D')}`;
};

/**
 * How a set of work dates stands against the signed week summaries:
 * { n: ticketed work days, k: of them under a Signed summary, stage } — the
 * stage is Signed when every day is, else the furthest-behind stage seen
 * (Draft < Validated < Sent < Signed), or '' with no summary at all.
 */
const STAGE_RANK = { Draft: 0, Declined: 0, Validated: 1, 'Sent for Signature': 2, Signed: 3 };
export const signedStanding = (dates, signedBy) => {
    const list = [...new Set(dates)].filter(Boolean);
    let k = 0;
    let stage = null;
    list.forEach((d) => {
        const s = signedBy?.[d]?.stage;
        if (s === 'Signed') k += 1;
        if (s && (stage == null || (STAGE_RANK[s] ?? 0) < (STAGE_RANK[stage] ?? 0))) stage = s;
    });
    if (list.length && k === list.length) stage = 'Signed';
    return { n: list.length, k, stage: stage || '' };
};

const newGroup = (key, label, week, extra) => ({
    po: key, key, label, week, rows: [], open: [], cost: 0, bill: 0, hrs: 0, dates: new Set(), vendor: '', ...extra,
});
const addRow = (g, t, showsHours) => {
    g.rows.push(t);
    if (!t.inv && !t.nbInv && !t.info) g.open.push(t);
    g.cost += t.cost || 0;
    if (!t.nb) g.bill += t.bill || 0;
    if (showsHours(t)) g.hrs += t.hrs || 0;
    if (ticketed(t) && t.date && !t.info) g.dates.add(t.date);
};

/**
 * Buckets the grid's rows by week ending, then by person / unit, with the
 * week's not-billable lines (plus the read-only `extra` labor cost) in one
 * trailing bucket. Returns { weeks, groups }: `groups` is every sub-bucket in
 * render order — the flat list the grid's existing group plumbing (expand
 * state, group checkboxes, the row budget) works off — and each week carries
 * its own `groups`, `open`, totals and signed standing.
 *
 *   rows       — the filtered grid rows, in grid sort order
 *   extra      — never-shown labor cost rows (already facet-filtered); marked
 *                `info` here so the grid renders them read-only
 *   signedBy   — work date -> { stage } (billingTicketDocs.signedByDate)
 *   showsHours — the grid's hours rule (labor + fleet usage)
 *   asc        — oldest week first (the grid's date sort ascending)
 */
export const weekGroups = (rows, { extra = [], signedBy = {}, showsHours = () => false, asc = false } = {}) => {
    const weeks = new Map();
    const week = (we) => {
        let w = weeks.get(we);
        if (!w) {
            w = { we, label: weekLabel(we), people: new Map(), nb: null, rows: [], open: [], cost: 0, bill: 0, hrs: 0, dates: new Set(), onSummary: 0 };
            weeks.set(we, w);
        }
        return w;
    };
    const nbOf = (w) => w.nb || (w.nb = newGroup(`${w.we}|${NB_GROUP_LABEL}`, NB_GROUP_LABEL, w.we, { nbGroup: true, info: 0 }));

    (rows || []).forEach((t) => {
        const w = week(weekEnding(t.date));
        addRow(w, t, showsHours);
        if (t.inv) w.onSummary += 1;
        if (t.nb) { addRow(nbOf(w), t, showsHours); return; }
        const label = unitOf(t);
        let g = w.people.get(label);
        if (!g) { g = newGroup(`${w.we}|${label}`, label, w.we); w.people.set(label, g); }
        addRow(g, t, showsHours);
    });
    (extra || []).forEach((r) => {
        const t = { ...r, info: true, nb: true, inv: null, hist: r.hist || [] };
        const w = week(weekEnding(t.date));
        addRow(w, t, showsHours);
        const g = nbOf(w);
        addRow(g, t, showsHours);
        g.info += 1;
    });

    const order = [...weeks.values()].sort((a, b) => (asc ? a.we.localeCompare(b.we) : b.we.localeCompare(a.we)));
    const groups = [];
    order.forEach((w) => {
        w.groups = [...w.people.values()].sort((a, b) => a.label.localeCompare(b.label));
        if (w.nb) {
            // Day order inside the bucket so a week's burden reads as a ledger.
            w.nb.rows.sort((a, b) => String(a.date).localeCompare(String(b.date)) || String(a.ct).localeCompare(String(b.ct)));
            w.groups.push(w.nb);
        }
        delete w.people;
        w.sig = signedStanding([...w.dates], signedBy);
        w.groups.forEach((g) => { g.sig = signedStanding([...g.dates], signedBy); groups.push(g); });
    });
    return { weeks: order, groups };
};
